import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, LinearProgress, Typography, Tabs, Tab } from '@mui/material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { MessageActions } from './MessageActions';
import { ISound } from '../../types/global/helper';

interface AudioMessageComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
    onReactionClick?: (emoji: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    formatMessageTime?: (timestamp?: string) => string;
}

export const AudioMessageComponent: React.FC<AudioMessageComponentProps> = ({
    message,
    isCurrentUser,
    onReactionClick = () => {},
    onDeleteMessage,
    formatMessageTime = () => '',
}) => {
    const { themeStyle } = useThemeContext();
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [selectedAudioIndex, setSelectedAudioIndex] = useState(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    // Ensure audio is always an array of sounds
    const audioFiles: ISound[] = Array.isArray(message.audio) ? message.audio : [];

    // Get the current audio URL
    const currentAudioFile = audioFiles.length > 0 ? audioFiles[selectedAudioIndex] : null;
    const audioUrl = currentAudioFile?.sound || '';
    const audioTitle = currentAudioFile?.title || `Audio ${selectedAudioIndex + 1}`;

    // Reset audio state when switching between audio files
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime(0);
        }
    }, [selectedAudioIndex]);

    // Create audio element
    useEffect(() => {
        if (!audioUrl) return;

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Add CORS handling
        audio.crossOrigin = 'anonymous';

        // Add error handling
        const handleError = (e: ErrorEvent) => {
            console.error('Audio playback error:', e);
            setIsPlaying(false);
            // Try to recover by recreating the audio element
            setTimeout(() => {
                if (audioRef.current) {
                    audioRef.current.load();
                }
            }, 1000);
        };

        // Event listeners
        audio.addEventListener('loadedmetadata', () => {
            setDuration(audio.duration);
        });

        audio.addEventListener('timeupdate', () => {
            setCurrentTime(audio.currentTime);
            setProgress((audio.currentTime / audio.duration) * 100);
        });

        audio.addEventListener('ended', () => {
            setIsPlaying(false);
            setProgress(0);
            setCurrentTime(0);
        });

        audio.addEventListener('error', handleError);

        return () => {
            audio.pause();
            audio.src = '';

            // Clean up event listeners
            audio.removeEventListener('loadedmetadata', () => {});
            audio.removeEventListener('timeupdate', () => {});
            audio.removeEventListener('ended', () => {});
            audio.removeEventListener('error', handleError);
        };
    }, [audioUrl]);

    const handlePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
                // Explicitly set playing state to false when pausing
                setIsPlaying(false);
            } else {
                // Add a promise to handle play() which returns a promise
                const playPromise = audioRef.current.play();

                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            // Playback started successfully
                            setIsPlaying(true);
                        })
                        .catch(error => {
                            // Auto-play was prevented or another error occurred
                            console.error('Audio play error:', error);
                            setIsPlaying(false);

                            // Check if it's an autoplay policy error
                            if (error.name === 'NotAllowedError') {
                                // Show a visual indication that user interaction is required
                                console.log('Autoplay prevented - user interaction required');
                            }
                        });
                } else {
                    // Older browsers might not return a promise
                    setIsPlaying(true);
                }
            }
        }
    };

    // Format time (e.g., 3:45)
    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    // Handle audio tab change
    const handleAudioChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedAudioIndex(newValue);
    };

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: '280px' },
                borderRadius: '16px',
                overflow: 'hidden',
                backgroundColor: theme[themeStyle].deep_grey,
                border: `1px solid ${theme[themeStyle].grey}30`,
                p: 2,
            }}>
            {/* Show tabs if multiple audio files */}
            {audioFiles.length > 1 && (
                <Tabs
                    value={selectedAudioIndex}
                    onChange={handleAudioChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        mb: 2,
                        '& .MuiTabs-indicator': {
                            backgroundColor: theme[themeStyle].highlight,
                        },
                        '& .MuiTab-root': {
                            color: theme[themeStyle].grey,
                            fontSize: '12px',
                            minWidth: 'auto',
                            p: 1,
                            minHeight: 'auto',
                            '&.Mui-selected': {
                                color: theme[themeStyle].highlight,
                            },
                        },
                    }}>
                    {audioFiles.map((audio, index) => (
                        <Tab key={index} label={audio.title || `Audio ${index + 1}`} value={index} />
                    ))}
                </Tabs>
            )}

            {/* Audio file title */}
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '16px',
                    color: theme[themeStyle].tag_white,
                    mb: 1,
                }}>
                {audioTitle}
            </Typography>

            {/* Audio player controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IconButton
                    onClick={handlePlayPause}
                    sx={{
                        color: theme[themeStyle].highlight,
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        mr: 2,
                        '&:hover': {
                            backgroundColor: theme[themeStyle].deep_grey_lighter + '80',
                        },
                    }}>
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>

                <Box sx={{ flex: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            backgroundColor: theme[themeStyle].grey + '40',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                        }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].grey,
                            }}>
                            {formatTime(currentTime)}
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: '12px',
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                color: theme[themeStyle].grey,
                            }}>
                            {formatTime(duration)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Message content if any */}
            {message.message_content && (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '14px',
                        color: theme[themeStyle].tag_white,
                        my: 2,
                    }}>
                    {message.message_content}
                </Typography>
            )}

            {/* Message info footer */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: isCurrentUser ? 'rgba(255, 255, 255, 0.7)' : theme[themeStyle].grey,
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                    }}>
                    {formatMessageTime(message.createdAt)}
                </Typography>

                {/* Add message actions */}
                <MessageActions
                    message={message}
                    isCurrentUser={isCurrentUser}
                    onReactionClick={onReactionClick}
                    onDeleteMessage={onDeleteMessage}
                />
            </Box>
        </Box>
    );
};
